import type { FullPreset } from '../../../core/assistant/models/ViewRules';

/**
 * Built-in viewpoint presets for the Compass Explorer. Registered with
 * the shared `viewRulesStore` via `registerDomainPresets()` — same seam
 * Network uses from `registerNetworkDomain()`.
 *
 * Each preset carries `domain: 'compass'`. `filterPresetsByDomain`
 * gates them by the active diagram's domain.
 *
 * Presets fall into two groups:
 *
 *  1. **Lens-equivalent presets** (`tier-colors`, `env-colors`,
 *     `team-colors`, `cost-heatmap`, `risk-heatmap`, `age-heatmap`,
 *     `exposure-colors`, `waste-heatmap`, `utilization-heatmap`) carry a `colorByField` or `heatmap` display
 *     rule. These replace the per-file lens modules that used to live
 *     under `src/domains/compass/lenses/`. The hook
 *     `useCompassExplorerView` turns the active preset into a
 *     `LensDefinition` via `presetAsLens()`, so the existing
 *     `attrs.lens.*` renderer contract stays intact.
 *
 *  2. **Filter-only presets** (`public-facing`, `data-tier`,
 *     `untagged`, `prod-only`, `cross-account-trust`, `stale`) carry
 *     a primary filter rule and no display rule — they narrow the
 *     scene rather than recolour it.
 *
 *  Provider icon lenses (aws/azure/gcp) remain in `LensRegistry` —
 *  the shared rule system has no icon-stamping rule kind.
 */
export const COMPASS_VIEW_PRESETS: FullPreset[] = [
  // ── Lens-equivalent presets ─────────────────────────────────────
  {
    id: 'compass-lens-tier',
    name: 'Tier colors',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: {},
        order: 0,
      },
      {
        name: 'Tier',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { colorByField: { field: 'tier' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-lens-env',
    name: 'Environment colors',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Environment',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { colorByField: { field: 'env' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-lens-team',
    name: 'Team colors',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Team',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { colorByField: { field: 'team' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: ['team'],
  },
  {
    id: 'compass-lens-cost',
    name: 'Cost heatmap',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Cost',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { heatmap: { field: 'cost', minColor: '#d1fae5', maxColor: '#ef4444' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: ['cost'],
  },
  {
    id: 'compass-lens-waste',
    name: 'Waste candidates',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Waste',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { heatmap: { field: 'waste', minColor: '#ecfeff', maxColor: '#dc2626' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: ['cost'],
  },
  {
    id: 'compass-lens-utilization',
    name: 'Utilization',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Utilization',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { heatmap: { field: 'utilization', minColor: '#dc2626', maxColor: '#22c55e', minValue: 0, maxValue: 100 } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: ['utilization'],
  },
  {
    id: 'compass-lens-risk',
    name: 'Risk heatmap',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Risk',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { heatmap: { field: 'risk', minColor: '#dbeafe', maxColor: '#b91c1c' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: ['risk'],
  },
  {
    id: 'compass-lens-age',
    name: 'Age heatmap',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Age',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { heatmap: { field: 'ageDays', minColor: '#d1fae5', maxColor: '#78350f' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: ['ageDays'],
  },
  {
    id: 'compass-lens-exposure',
    name: 'Exposure colors',
    domain: 'compass',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Exposure',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: { colorByField: { field: 'publicExposure' } },
        order: 1,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },

  // ── Filter-only presets ─────────────────────────────────────────
  {
    id: 'compass-filter-public-facing',
    name: 'Public-facing only',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [
          { id: 'public-exposure', field: 'publicExposure', operator: 'equals', value: true },
        ],
        logic: 'and',
        enabled: true,
        display: { visibility: { filterEffect: 'dim', showReportingLines: false } },
        order: 0,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-filter-data-tier',
    name: 'Data-tier only',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [
          { id: 'data-tier', field: 'tier', operator: 'equals', value: 'data' },
        ],
        logic: 'and',
        enabled: true,
        display: { visibility: { filterEffect: 'dim', showReportingLines: false } },
        order: 0,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-filter-untagged',
    name: 'Untagged resources',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [
          { id: 'no-owner', field: 'owner', operator: 'is_empty', value: null },
        ],
        logic: 'or',
        enabled: true,
        display: { visibility: { filterEffect: 'dim', showReportingLines: false } },
        order: 0,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-filter-prod-only',
    name: 'Production only',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [
          { id: 'prod-env', field: 'env', operator: 'equals', value: 'prod' },
          { id: 'production-env', field: 'env', operator: 'equals', value: 'production' },
        ],
        logic: 'or',
        enabled: true,
        display: { visibility: { filterEffect: 'hide', showReportingLines: false } },
        order: 0,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-filter-cross-account',
    name: 'Cross-account trust',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [
          { id: 'cross-acct', field: 'crossAccount', operator: 'equals', value: true },
        ],
        logic: 'and',
        enabled: true,
        display: { visibility: { filterEffect: 'dim', showReportingLines: false } },
        order: 0,
      },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'compass-filter-stale',
    name: 'Stale resources',
    domain: 'compass',
    rules: [
      {
        name: 'Primary Filter',
        conditions: [
          { id: 'stale-age', field: 'ageDays', operator: 'greater_than', value: 180 },
        ],
        logic: 'and',
        enabled: true,
        display: { visibility: { filterEffect: 'dim', showReportingLines: false } },
        order: 0,
      },
    ],
    createdAt: 0,
    requiredFields: ['ageDays'],
  },
];

/** IDs whose display rule is evaluated as a lens (colorByField / heatmap). */
export const COMPASS_LENS_PRESET_IDS: readonly string[] = [
  'compass-lens-tier',
  'compass-lens-env',
  'compass-lens-team',
  'compass-lens-cost',
  'compass-lens-waste',
  'compass-lens-utilization',
  'compass-lens-risk',
  'compass-lens-age',
  'compass-lens-exposure',
] as const;
